import { loadStripe, Stripe } from '@stripe/stripe-js';
import { getRemoteConfig, getValue } from 'firebase/remote-config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from './firebase';
import { t } from '../i18n';

let stripePromise: Promise<Stripe | null> | null = null;

/**
 * Validate that the key is a publishable key (not a secret key)
 */
const isPublishableKey = (key: string): boolean => {
  return key.startsWith('pk_test_') || key.startsWith('pk_live_');
};

/**
 * Get Stripe publishable key from Firebase Remote Config or environment variable
 * SECURITY: This key is safe to expose client-side (publishable key only)
 */
export const getStripePublishableKey = (): string => {
  try {
    const remoteConfig = getRemoteConfig();
    const stripeKey = getValue(remoteConfig, 'stripe_publishable_key');

    if (stripeKey && stripeKey.asString()) {
      const key = stripeKey.asString();

      // SECURITY CHECK: Ensure it's not a secret key
      if (!isPublishableKey(key)) {
        console.error('SECURITY WARNING: Invalid Stripe key format detected in Remote Config');
        return '';
      }

      return key;
    }
  } catch (error) {
    console.warn('Failed to get Stripe key from Remote Config:', error);
  }

  // Fallback to environment variable
  const envKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  if (!envKey) {
    console.error('Stripe publishable key not found in Remote Config or environment variables');
    return '';
  }

  // SECURITY CHECK: Ensure it's not a secret key
  if (!isPublishableKey(envKey)) {
    console.error('SECURITY WARNING: Invalid Stripe key format in environment variables');
    return '';
  }

  return envKey;
};

/**
 * Get Stripe instance (singleton pattern)
 */
export const getStripe = (): Promise<Stripe | null> => {
  if (!stripePromise) {
    const publishableKey = getStripePublishableKey();
    if (!publishableKey) {
      console.error('Cannot initialize Stripe without publishable key');
      return Promise.resolve(null);
    }
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

/**
 * Create a payment intent for subscription
 */
export const createPaymentIntent = async (
  amount: number,
  currency: string = 'gbp',
  metadata?: Record<string, string>
): Promise<{ clientSecret: string; paymentIntentId: string } | null> => {
  try {
    // This should call your backend API endpoint
    // For now, this is a placeholder - you'll need to implement the backend
    const response = await fetch('/api/create-payment-intent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount,
        currency,
        metadata
      })
    });

    if (!response.ok) {
      throw new Error(t('errors.stripe.failedPaymentIntent'));
    }

    const data = await response.json();
    return {
      clientSecret: data.clientSecret,
      paymentIntentId: data.paymentIntentId
    };
  } catch (error) {
    console.error('Error creating payment intent:', error);
    return null;
  }
};

/**
 * Create a Stripe Checkout Session for subscription
 */
export const createCheckoutSession = async (
  priceId: string,
  userId: string,
  email: string,
  successUrl: string,
  cancelUrl: string
): Promise<{ sessionId: string } | null> => {
  try {
    // This should call your backend API endpoint
    const response = await fetch('/api/create-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        priceId,
        userId,
        email,
        successUrl,
        cancelUrl
      })
    });

    if (!response.ok) {
      throw new Error(t('errors.stripe.failedCheckoutSession'));
    }

    const data = await response.json();
    return { sessionId: data.sessionId };
  } catch (error) {
    console.error('Error creating checkout session:', error);
    return null;
  }
};

/**
 * Cancel a Stripe subscription using Firebase Cloud Function
 */
export const cancelStripeSubscription = async (
  userId: string,
  subscriptionId?: string,
  cancelAtPeriodEnd: boolean = true
): Promise<{ success: boolean; message?: string; data?: any } | null> => {
  try {
    // Initialize Cloud Functions
    const functions = getFunctions(app, 'europe-west2');
    const cancelSubscription = httpsCallable(functions, 'cancelStripeSubscription');

    // If subscriptionId is not provided, we need to get it from the user's data
    if (!subscriptionId) {
      throw new Error(t('errors.stripe.subscriptionIdRequired'));
    }

    // Call the cloud function
    const result = await cancelSubscription({
      subscriptionId,
      cancelAtPeriodEnd
    });

    const data = result.data as any;

    if (data.success) {
      const message = cancelAtPeriodEnd
        ? t('stripe.cancelledAtPeriodEnd', { date: new Date((data.currentPeriodEnd || 0) * 1000).toLocaleDateString() })
        : t('stripe.cancelledImmediately');

      return {
        success: true,
        message,
        data
      };
    } else {
      throw new Error(t('errors.stripe.failedCancelSubscription'));
    }
  } catch (error) {
    console.error('Error canceling subscription:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : t('errors.stripe.failedCancelSubscription')
    };
  }
};

/**
 * Resume a cancelled Stripe subscription using Firebase Cloud Function
 */
export const resumeStripeSubscription = async (
  userId: string,
  subscriptionId?: string
): Promise<{ success: boolean; message?: string; data?: any } | null> => {
  try {
    // Initialize Cloud Functions
    const functions = getFunctions(app, 'europe-west2');
    const resumeSubscription = httpsCallable(functions, 'resumeStripeSubscription');

    // If subscriptionId is not provided, we need to get it from the user's data
    if (!subscriptionId) {
      throw new Error(t('errors.stripe.subscriptionIdRequired'));
    }

    // Call the cloud function
    const result = await resumeSubscription({
      subscriptionId
    });

    const data = result.data as any;

    if (data.success) {
      return {
        success: true,
        message: t('stripe.resumedSuccessfully'),
        data
      };
    } else {
      throw new Error(t('errors.stripe.failedResumeSubscription'));
    }
  } catch (error) {
    console.error('Error resuming subscription:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : t('errors.stripe.failedResumeSubscription')
    };
  }
};

/**
 * Update/Change a Stripe subscription to a different plan/price using Firebase Cloud Function
 */
export const updateStripeSubscription = async (
  userId: string,
  newPriceId: string,
  prorationBehavior: 'create_prorations' | 'none' | 'always_invoice' = 'create_prorations'
): Promise<{ success: boolean; message?: string; data?: any } | null> => {
  try {
    // Initialize Cloud Functions
    const functions = getFunctions(app, 'europe-west2');
    const updateSubscription = httpsCallable(functions, 'updateStripeSubscription');

    if (!userId || !newPriceId) {
      throw new Error(t('errors.stripe.userIdAndPriceRequired'));
    }

    // Call the cloud function - backend will find the subscription
    const result = await updateSubscription({
      userId,
      newPriceId,
      prorationBehavior
    });

    const data = result.data as any;

    if (data.success) {
      return {
        success: true,
        message: t('stripe.updatedSuccessfully'),
        data
      };
    } else {
      throw new Error(data.message || t('errors.stripe.failedUpdateSubscription'));
    }
  } catch (error) {
    console.error('Error updating subscription:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : t('errors.stripe.failedUpdateSubscription')
    };
  }
};
